import { useMemo } from 'react';
import { Link } from '../../router/Link';
import {
  Button,
  ConfirmButton,
  LoadingStatus,
  Notice,
  ProblemPanel,
  Spinner,
  StatePanel,
  Thumbnail,
} from '../../ui';
import { CardActions } from '../../listing/CardActions';
import { FeedGridSkeleton } from '../../listing/FeedGrid';
import { ThumbnailOverlay } from '../../listing/ThumbnailOverlay';
import { GridSearch } from '../../listing/GridSearch';
import { useAutoLoadMore } from '../../listing/useAutoLoadMore';
import { useGridFocus } from '../../listing/useGridFocus';
import { useGridPreview } from '../../listing/useGridPreview';
import { AddToPlaylist } from '../../playlists/AddToPlaylist';
import { WatchLaterButton } from '../../playlists/WatchLaterButton';
import { AddToQueue } from '../../queue/AddToQueue';
import { QueueCardAsk } from '../../queue/QueueCardAsk';
import { useQueueCardAsk } from '../../queue/useQueueCardAsk';
import { useQueueCardPress } from '../../queue/useQueueCardPress';
import { useDeArrowBranding } from '../../dearrow/useDeArrowBranding';
import { useHistory } from './useHistory';
import { historyQueueCandidate, type HistoryRowView } from './model';
import layout from '../../ui/layout.module.css';
import styles from './history.module.css';
import { useCopy } from '../../strings/useCopy';

/**
 * §8's History page. Renders props (R13) — every href arrived built, every string arrived formatted, and
 * the only branches are on `state.kind` and on whether a field exists.
 *
 * **The feed grid, in `layout.wide`, like Home, Trending and Subscriptions.** 3d built this as a
 * one-column list and argued for it — a history is read chronologically, every row carries an action, and
 * half the rows have no title yet — and the argument was answered by the page next to it: four of §8's
 * five nav destinations drew cards and this one drew rows, at a different inset, because the gate renders
 * its children verbatim and this page set no frame at all. Each of the three reasons has an answer inside
 * the grid: the reading order is the grid's order (left to right, top to bottom, which is how every feed
 * in the app is read), the Remove button sits over the frame where it has room and is revealed on hover or
 * focus, and a row with no title yet draws its id in the title's place rather than an empty box.
 */
export function HistoryView() {
  const binding = useHistory();
  const { state } = binding;
  const copy = useCopy().text;
  const words = copy.history.page;
  const searchWords = copy.listing.search;
  const searching = binding.search.trim() !== '';
  // Suspended while a search is set — the observer would otherwise chase matches through the whole
  // history the moment a query empties the visible page. The button stays for a deliberate press.
  const autoLoad = useAutoLoadMore(
    state.kind === 'ready' && state.nextPage !== null && !binding.loadingMore && !searching,
    binding.loadMore,
  );

  return (
    <div className={layout.wide}>
      <section className={styles.page} aria-busy={state.kind === 'loading' ? true : undefined}>
        <header className={styles.head}>
          <div className={styles.headText}>
            <h1 className={styles.heading}>
              {words.heading}
              {binding.refreshing ? (
                <span className={styles.refreshing}>
                  <Spinner />
                </span>
              ) : null}
            </h1>
            <p className={styles.lede}>{words.lede}</p>
          </div>

          {state.kind === 'ready' ? (
            /*
             * The other pages' Manage button, by shape and by place — variant, size and the
             * top-right corner. Still two clicks to destroy everything: the arming moved into the
             * button itself (ConfirmButton), and what the second click deletes rides the armed
             * state's title, count included, since there is no panel to say it any more.
             */
            <ConfirmButton
              variant="default"
              size="sm"
              disabled={binding.clear.busy}
              confirmLabel={copy.shell.confirm}
              confirmTitle={words.confirmDetail}
              onConfirm={binding.clear.act}
            >
              {binding.clear.busy ? words.clearing : words.clear}
            </ConfirmButton>
          ) : null}
        </header>

        {binding.writeProblem === null ? null : (
          <Notice tone="caution">
            {binding.writeProblem.title}. {binding.writeProblem.detail}
          </Notice>
        )}

        {state.kind === 'loading' ? (
          <>
            <LoadingStatus>{words.loading}</LoadingStatus>
            {/* The feed's own skeleton since the grid is the feed's — one fewer copy of the card box to
                keep in step with the real card. */}
            <FeedGridSkeleton cards={state.skeletonRows} />
          </>
        ) : null}

        {state.kind === 'problem' ? <ProblemPanel copy={state.copy} onRetry={binding.retry} /> : null}

        {state.kind === 'empty' ? <StatePanel title={state.title} detail={state.detail} /> : null}

        {state.kind === 'ready' ? (
          <>
            {state.notices.map((notice) => (
              <Notice key={notice.id} tone={notice.tone === 'info' ? 'info' : 'caution'}>
                {notice.text}
              </Notice>
            ))}

            {/* The controls line above the grid (CLAUDE.md) — this page has no filters,
                so the line is the search alone, on the right. It matches hydrated titles only:
                the endpoint serves bare ids, and searching must not trigger the lookups. */}
            <div className={styles.controls}>
              <GridSearch
                id="search-history"
                value={binding.search}
                onChange={binding.setSearch}
                words={searchWords}
                className={styles.controlsSearch}
              />
            </div>

            {state.nothingMatches ? (
              <p className={styles.noMatches}>{searchWords.noMatches(binding.search.trim())}</p>
            ) : (
              <HistoryGrid rows={state.rows} observeRow={binding.observeRow} onRemove={binding.remove} />
            )}

            {/* A finished list simply ends — the "that is all N" sentence is retired. */}
            <div className={styles.paging} ref={autoLoad}>
              {state.nextPage === null ? null : (
                <Button onClick={binding.loadMore} disabled={binding.loadingMore}>
                  {binding.loadingMore ? words.loadingMore : words.loadMore(state.rows.length)}
                </Button>
              )}
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}

/**
 * The cards. One tab stop for the whole grid via `useGridFocus`, which matters more here than on a feed: a
 * history of 200 rows is 600 tab stops as an ordinary list of links — and the Remove button is a fourth
 * per card, which is why it takes the row's roving `tabIndex` rather than its own.
 *
 * `ref` per card is what reports visibility, and it is a callback ref rather than a mount effect because
 * "mounted" and "on screen" are different questions — the whole page mounts at once, and being on screen
 * is what costs the 89–262 KB lookup.
 */
function HistoryGrid({
  rows,
  observeRow,
  onRemove,
}: {
  readonly rows: readonly HistoryRowView[];
  readonly observeRow: (videoId: string, element: Element | null) => void;
  readonly onRemove: (videoId: string) => void;
}) {
  const { attachGrid, onKeyDown, tabIndexFor, onFocusIndex } = useGridFocus(rows.length);
  const words = useCopy().text.history.page;

  return (
    <ul className={styles.grid} ref={attachGrid} onKeyDown={onKeyDown} aria-label={words.listLabel}>
      {rows.map((row, index) => (
        <HistoryCard
          key={row.videoId}
          row={row}
          tabIndex={tabIndexFor(index)}
          onFocus={() => onFocusIndex(index)}
          observeRow={observeRow}
          onRemove={onRemove}
        />
      ))}
    </ul>
  );
}

/**
 * One row. Its own component since DeArrow (2026-08-02): a hook cannot be called inside
 * `rows.map`, and the row wants one — the community title where one exists, over the hydrated one,
 * over the bare id.
 */
function HistoryCard({
  row,
  tabIndex,
  onFocus,
  observeRow,
  onRemove,
}: {
  readonly row: HistoryRowView;
  readonly tabIndex: number;
  readonly onFocus: () => void;
  readonly observeRow: (videoId: string, element: Element | null) => void;
  readonly onRemove: (videoId: string) => void;
}) {
  const copy = useCopy().text;
  const words = copy.history.page;
  const dearrow = useDeArrowBranding(row.videoId);
  const title = dearrow.title ?? row.title;
  // The feed grid's hover preview, on this grid too (2026-08-02) — it was wired to
  // `ListingCard` alone, so the two grids that draw their own card had thumbnails that never moved.
  // A dead row has nothing behind it to preview.
  const preview = useGridPreview({
    videoId: row.videoId,
    durationSec: row.durationSec,
    noPreview: row.unavailable,
  });
  /*
   * The queue's view of this row, and what a press on it means while a list is running
   * (`queue/useQueueCardPress.ts`). A dead row gets neither: queueing a video YouTube no longer
   * serves builds a list with a hole in it, and the queue's own page has no way to say why.
   */
  const queueItem = useMemo(() => historyQueueCandidate(row, title), [row, title]);
  const onPress = useQueueCardPress(queueItem);
  const pressVideo = row.unavailable ? undefined : onPress;
  const asking = useQueueCardAsk(row.unavailable ? null : queueItem);

  return (
    <li
      className={row.unavailable ? styles.cardGone : styles.card}
      ref={(element) => observeRow(row.videoId, element)}
      onFocus={onFocus}
    >
      <Link className={styles.thumb} href={row.href} tabIndex={-1} ariaHidden onClick={pressVideo}>
        <Thumbnail
          src={dearrow.thumbnailUrl ?? row.thumbnailUrl}
          badge={row.badge}
          progress={preview.progress ?? row.progress}
          onPointerEnter={preview.onPointerEnter}
          onPointerLeave={preview.onPointerLeave}
          onPointerMove={preview.onPointerMove}
          overlay={<ThumbnailOverlay preview={preview} />}
        />
      </Link>

      <div className={styles.text}>
        <Link
          className={styles.title}
          href={row.href}
          title={title ?? undefined}
          tabIndex={tabIndex}
          onClick={pressVideo}
        >
          {row.marker === null ? null : (
            <span className={styles.marker}>{row.marker.label} — </span>
          )}
          {/*
           * The id, until the title arrives. Not a spinner and not a skeleton bar: the row is a real
           * link to a real video the whole time — only its *name* is pending — and an id is what this
           * endpoint actually gave us. A skeleton here would claim the row itself was still loading.
           */}
          {title ?? <span className={styles.idOnly}>{row.videoId}</span>}
        </Link>

        {row.author === null ? null : (
          <div className={styles.byline}>
            {row.authorHref === null ? (
              // No link: a collaboration video has no single channel to point at.
              <span className={styles.author}>{row.author}</span>
            ) : (
              <Link className={styles.author} href={row.authorHref} tabIndex={tabIndex}>
                {row.author}
              </Link>
            )}
            {row.authorVerified ? (
              <span className={styles.verified} title={words.verified}>
                ✓
              </span>
            ) : null}
          </div>
        )}

        {row.viewCountText === null && row.publishedText === null ? null : (
          <div className={styles.meta}>
            {row.viewCountText === null ? null : (
              <span title={row.viewCountTitle ?? undefined}>
                {copy.listing.meta.views(row.viewCountText)}
              </span>
            )}
            {row.publishedText === null ? null : (
              <span title={row.publishedTitle ?? undefined}>{row.publishedText}</span>
            )}
          </div>
        )}
      </div>

      {/*
       * Over the frame's top corner rather than in a column of its own, which is what a card has room
       * for. `listing/CardActions.tsx` says why it is a sibling of the thumbnail link and why it is
       * written after the text it is drawn above.
       */}
      <CardActions covered={asking}>
        {/* Armed by the first click, red with the confirm word; acting on the second. */}
        <ConfirmButton
          size="sm"
          variant="media"
          icon
          tabIndex={tabIndex}
          label={row.removing ? words.removing : words.remove(title ?? row.videoId)}
          /* The hover word: the short act, not the accessible name — the card is already
             showing the title an inch below, so repeating it in a tooltip over the picture is
             noise. Armed, `confirmTitle` still names the video, which is where words help. */
          title={row.removing ? words.removing : words.removeTitle}
          confirmLabel={copy.shell.confirm}
          confirmTitle={words.remove(title ?? row.videoId)}
          disabled={row.removing}
          onConfirm={() => onRemove(row.videoId)}
        >
          {row.removing ? <Spinner /> : <RemoveGlyph />}
        </ConfirmButton>
        {/* The rule for a card that already has a remove: Watch later goes below it, and Save
            to playlist below that. */}
        <WatchLaterButton
          face="corner"
          videoId={row.videoId}
          videoTitle={title ?? row.videoId}
          tabIndex={tabIndex}
        />
        <AddToPlaylist
          face="corner"
          videoId={row.videoId}
          videoTitle={title ?? row.videoId}
          tabIndex={tabIndex}
        />
        {/* Last in the stack, and the only one of the four that renders signed out: a queue belongs
            to this browser tab, so there is no account for it to need (`queue/AddToQueue.tsx`). */}
        {row.unavailable ? null : <AddToQueue item={queueItem} tabIndex={tabIndex} />}
      </CardActions>

      {/*
       * The two answers a press offers while a queue is running, over this card's picture and only
       * while this is the card that was pressed (`queue/QueueCardAsk.tsx`). A dead row never asks,
       * for the same reason it has no queue button: there is no video behind it.
       */}
      <QueueCardAsk item={row.unavailable ? null : queueItem} tabIndex={tabIndex} />
    </li>
  );
}

function RemoveGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 12h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
